/**
 * FridgeIQ — Login Screen
 */

import { useState } from "react";
import { signIn, confirmSignIn } from "aws-amplify/auth";

export default function Login({ onAuthenticated }) {
  const [step, setStep] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPass] = useState("");
  const [mfaCode, setMfa] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSignIn = async () => {
    setError("");
    if (!email || !password) {
      setError("Enter email and password");
      return;
    }
    setLoading(true);
    try {
      const { nextStep, isSignedIn } = await signIn({
        username: email,
        password,
      });
      console.log("signIn result:", { nextStep, isSignedIn });
      if (isSignedIn) {
        onAuthenticated();
      } else if (nextStep?.signInStep?.includes("SMS")) {
        setStep("mfa");
      } else if (
        nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_SMS_MFA_CODE" ||
        nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_SMS_CODE"
      ) {
        setStep("mfa");
      } else if (
        nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
      ) {
        setStep("newpassword");
      } else {
        setError(`Unexpected step: ${nextStep?.signInStep}`);
      }
    } catch (err) {
      console.error("signIn error:", err);
      setError(err.message);
    }
    setLoading(false);
  };

  const handleMfa = async () => {
    setError("");
    if (!mfaCode) {
      setError("Enter the SMS code");
      return;
    }
    setLoading(true);
    try {
      const { isSignedIn } = await confirmSignIn({
        challengeResponse: mfaCode,
      });
      if (isSignedIn) onAuthenticated();
      else setError("MFA failed");
    } catch (err) {
      console.error("MFA error:", err);
      setError(err.message);
    }
    setLoading(false);
  };

  const handleNewPassword = async () => {
    setError("");
    if (!newPass) {
      setError("Enter a new password");
      return;
    }
    if (newPass !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      const { nextStep, isSignedIn } = await confirmSignIn({
        challengeResponse: newPass,
      });
      console.log("newPassword result:", { nextStep, isSignedIn });
      if (isSignedIn) onAuthenticated();
      else if (nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_SMS_MFA_CODE")
        setStep("mfa");
      else setError(`Next: ${nextStep?.signInStep}`);
    } catch (err) {
      console.error("newPassword error:", err);
      setError(err.message);
    }
    setLoading(false);
  };

  const s = {
    wrap: {
      minHeight: "100vh",
      background: "#080c0a",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: "'DM Sans', system-ui, sans-serif",
      padding: 20,
    },
    card: {
      width: "100%",
      maxWidth: 400,
      background: "rgba(255,255,255,0.03)",
      border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: 16,
      padding: 32,
    },
    input: {
      display: "block",
      width: "100%",
      background: "rgba(255,255,255,0.06)",
      border: "1px solid rgba(255,255,255,0.12)",
      borderRadius: 10,
      padding: "12px 16px",
      color: "#f9fafb",
      fontSize: 14,
      outline: "none",
      marginBottom: 12,
      boxSizing: "border-box",
    },
    btn: {
      display: "block",
      width: "100%",
      background: "linear-gradient(135deg, #10b981, #059669)",
      border: "none",
      borderRadius: 10,
      padding: 13,
      color: "white",
      fontSize: 14,
      fontWeight: 700,
      cursor: "pointer",
      marginTop: 8,
    },
    title: { fontSize: 20, fontWeight: 700, color: "#f9fafb", marginBottom: 6 },
    sub: { fontSize: 13, color: "#6b7280", marginBottom: 22 },
    err: { color: "#f87171", fontSize: 12, marginBottom: 10 },
  };

  return (
    <div style={s.wrap}>
      <div>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 14,
              fontSize: 26,
              background: "linear-gradient(135deg, #10b981, #059669)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 12px",
            }}
          >
            🧊
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#f9fafb" }}>
            FridgeIQ
          </div>
          <div
            style={{
              fontSize: 11,
              color: "#4b5563",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
            }}
          >
            Operator Portal
          </div>
        </div>

        <div style={s.card}>
          {step === "signin" && (
            <>
              <div style={s.title}>Sign in</div>
              <div style={s.sub}>Enter your credentials to continue</div>
              <input
                style={s.input}
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <input
                style={s.input}
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPass(e.target.value)}
              />
              {error && <div style={s.err}>⚠️ {error}</div>}
              <button style={s.btn} onClick={handleSignIn} disabled={loading}>
                {loading ? "Signing in..." : "Sign In →"}
              </button>
            </>
          )}

          {step === "mfa" && (
            <>
              <div style={s.title}>Check your phone</div>
              <div style={s.sub}>Enter the 6-digit code sent via SMS</div>
              <input
                style={{
                  ...s.input,
                  textAlign: "center",
                  fontSize: 24,
                  letterSpacing: "0.3em",
                }}
                type="text"
                inputMode="numeric"
                placeholder="000000"
                value={mfaCode}
                onChange={(e) =>
                  setMfa(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
              />
              {error && <div style={s.err}>⚠️ {error}</div>}
              <button style={s.btn} onClick={handleMfa} disabled={loading}>
                {loading ? "Verifying..." : "Verify →"}
              </button>
              <button
                onClick={() => {
                  setStep("signin");
                  setError("");
                }}
                style={{
                  display: "block",
                  width: "100%",
                  background: "none",
                  border: "none",
                  color: "#4b5563",
                  fontSize: 13,
                  marginTop: 12,
                  cursor: "pointer",
                }}
              >
                ← Back
              </button>
            </>
          )}

          {step === "newpassword" && (
            <>
              <div style={s.title}>Set your password</div>
              <div style={s.sub}>First sign in — set a permanent password</div>
              <input
                style={s.input}
                type="password"
                placeholder="New password"
                value={newPass}
                onChange={(e) => setNewPass(e.target.value)}
              />
              <input
                style={s.input}
                type="password"
                placeholder="Confirm password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
              {error && <div style={s.err}>⚠️ {error}</div>}
              <button
                style={s.btn}
                onClick={handleNewPassword}
                disabled={loading}
              >
                {loading ? "Setting..." : "Set Password →"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
